<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { AlertTriangle, AlertOctagon, Check } from 'lucide-vue-next'
import type { DashboardAlert } from './dashboard-process'

defineProps<{
    alerts: DashboardAlert[]
}>()

const { t } = useI18n()

function alertText(a: DashboardAlert): string {
    if (a.kind === 'DEVICE_OFFLINE') return t('dashboard.alerts.deviceOffline', { name: a.relatedName })
    if (a.kind === 'EVENT_NO_SCANS') return t('dashboard.alerts.eventNoScans', { name: a.relatedName })
    if (a.kind === 'BROKEN_TICKETSET') return t('dashboard.alerts.brokenTicketset', { name: a.relatedName })
    return t('dashboard.alerts.unknown')
}

function alertLink(a: DashboardAlert): string {
    if (a.relatedType === 'device') return `/devices?id=${a.relatedId}`
    if (a.relatedType === 'event') return `/events?id=${a.relatedId}`
    if (a.relatedType === 'ticketset') return `/ticketsets?id=${a.relatedId}`
    return '#'
}
</script>

<template>
    <section class="mb-6">
        <div class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{{ t('dashboard.alerts.title') }}</div>
        <div class="flex gap-2 flex-wrap items-center">
            <RouterLink
                v-for="(a, i) in alerts"
                :key="`${a.kind}-${a.relatedId}-${i}`"
                :to="alertLink(a)"
                :class="[
                    'inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg border hover:shadow-sm transition cursor-pointer shrink-0',
                    a.severity === 'DANGER' ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                ]"
            >
                <span
                    :class="[
                        'w-5 h-5 rounded-md flex items-center justify-center shrink-0',
                        a.severity === 'DANGER' ? 'bg-red-100' : 'bg-orange-100'
                    ]"
                >
                    <component
                        :is="a.severity === 'DANGER' ? AlertOctagon : AlertTriangle"
                        :class="['w-3 h-3', a.severity === 'DANGER' ? 'text-red-600' : 'text-orange-600']"
                    />
                </span>
                <span :class="['text-xs font-semibold', a.severity === 'DANGER' ? 'text-red-700' : 'text-orange-700']">
                    {{ alertText(a) }}
                </span>
            </RouterLink>
            <span
                v-if="alerts.length === 0"
                class="inline-flex items-center gap-1.5 text-xs text-gray-500 pl-1"
            >
                <span class="w-5 h-5 rounded-md bg-green-100 flex items-center justify-center shrink-0">
                    <Check class="w-3 h-3 text-green-600" />
                </span>
                <span>{{ t('dashboard.alerts.empty') }}</span>
            </span>
        </div>
    </section>
</template>
